import { fail, success } from '@helpers/result-helper.js'
import toast from '@services/toasts/index.js'
import { isSuccess, isHttpStatus } from '@/helpers/http-status-helpers'
import dayjs from '@/services/date/index.js'
import { isCacheFresh } from '@/helpers/cache-helpers'
import getSelectedDayText from '@/helpers/get-replace-me-selected-day-text'
import { VuexResponse } from '@/helpers/vuex-action-builder'
// eslint-disable-next-line
import { BookingOverviewCountsViewModel } from '@/models/bookings/responses/bookingOverviewCountsViewModel'
import { DurationUnits } from '@/shared/constants/date/DurationUnits'

const getDefaultState = () => {
  return {
    // Place any new state properties here
    loadingCount: 0,
    bookingSummaryLoadingCount: 0,
    crudLoadingCount: 0,
    editBookingLoadingCount: 0,
    feedbackFormUrlLoadingCount: 0,
    quickFeedbackSubmittingCount: 0,
    replaceMeStatusCheckLoadingCount: 0,
    submitReplaceMeBookingLoadingCount: 0,
    cancelBookingLoadingCount: 0,
    extendBookingLoadingCount: 0,
    exportFileCount: 0,
    fetchBookingCount: 0,
    homeRefreshCount: 0,
    recentLocations: [],
    replaceMeStatusChecks: [],
    bookingOverview: [],
    bookingList: [],
    exportData: null,
    filters: [],
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,
  getters: {
    moduleName: () => 'bookings',
    isLoading: (state) => state.loadingCount > 0,
    isLoadingBookingSummary: (state) => state.bookingSummaryLoadingCount > 0,
    isLoadingCRUD: (state) => state.crudLoadingCount > 0,
    isLoadingEditBooking: (state) => state.editBookingLoadingCount > 0,
    recentLocations: (state) => state.recentLocations,
    isLoadingFeedbackFormUrl: (state) => state.feedbackFormUrlLoadingCount > 0,
    isLoadingReplaceMeStatusCheck: (state) =>
      state.replaceMeStatusCheckLoadingCount > 0,
    isSubmittingQuickFeedback: (state) =>
      state.quickFeedbackSubmittingCount > 0,
    replaceMeStatusChecks: (state) => (clientId) =>
      state.replaceMeStatusChecks.find((x) => x.clientId === clientId),
    isSubmitReplaceMeLoading: (state) =>
      state.submitReplaceMeBookingLoadingCount > 0,
    isLoadingCancelBooking: (state) => state.cancelBookingLoadingCount > 0,
    isLoadingExtendBooking: (state) => state.extendBookingLoadingCount > 0,
    isExportFile: (state) => state.exportFileCount > 0,
    isFetchBooking: (state) => state.fetchBookingCount > 0,
    bookingOverview: (state) => state.bookingOverview,
    homeRefreshCount: (state) => state.homeRefreshCount,
    bookingList: (state) => state.bookingList.bookings,
    approvalRequiredCount: (state) => state.bookingList.approvalRequiredCount,
    bookingListTotalPages: (state) => state.bookingList.totalPages,
    bookingListClients: (state) => state.bookingList.clients,
    bookingListFilter: (state) => state.bookingList.filters,
    getOverviewTotalBookingsCountByDay: (state) => (day) => {
      const dateFound = state.bookingOverview.find((booking) =>
        dayjs(booking.dateLocal).isSame(dayjs(day), DurationUnits.DAY)
      )
      return dateFound?.totalBookingsCount || 0
    },
  },
  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_SELECTED_DATE_BOOKINGS(state) {
      state.bookingSummaryLoadingCount++
    },
    FINISH_LOADING_SELECTED_DATE_BOOKINGS(state) {
      state.bookingSummaryLoadingCount--
    },
    START_LOADING_BOOKINGS_OVERVIEW(state) {
      state.bookingSummaryLoadingCount++
    },
    FINISH_LOADING_BOOKINGS_OVERVIEW(state) {
      state.bookingSummaryLoadingCount--
    },
    START_LOADING_REPLACE_ME_STATUS_CHECK(state) {
      state.replaceMeStatusCheckLoadingCount++
    },
    FINISH_LOADING_REPLACE_ME_STATUS_CHECK(state) {
      state.replaceMeStatusCheckLoadingCount--
    },
    START_LOADING_SUBMIT_REPLACE_ME(state) {
      state.submitReplaceMeBookingLoadingCount++
    },
    FINISH_LOADING_SUBMIT_REPLACE_ME(state) {
      state.submitReplaceMeBookingLoadingCount--
    },
    START_LOADING_CANCEL_BOOKING(state) {
      state.cancelBookingLoadingCount++
    },
    FINISH_LOADING_CANCEL_BOOKING(state) {
      state.cancelBookingLoadingCount--
    },
    START_LOADING_EXTEND_BOOKING(state) {
      state.extendBookingLoadingCount++
    },
    FINISH_LOADING_EXTEND_BOOKING(state) {
      state.extendBookingLoadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    START_LOADING_EDIT_BOOKING(state) {
      state.editBookingLoadingCount++
    },
    FINISH_LOADING_EDIT_BOOKING(state) {
      state.editBookingLoadingCount--
    },
    SET_RECENT_BOOKING_LOCATION(state, locationId) {
      const found = state.recentLocations.find(
        (x) => x.locationId === locationId
      )

      if (found) {
        found.timestamp = dayjs()
        return
      }

      state.recentLocations.push({ locationId, timestamp: dayjs() })
    },
    /**
     * @param {*} state
     * @param {BookingOverviewCountsViewModel[]} data
     */
    SET_BOOKING_OVERVIEW(state, data) {
      state.bookingOverview = data
    },
    CLEAR_STORE(state) {
      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
    START_LOADING_FEEDBACK_FORM_URL(state) {
      state.feedbackFormUrlLoadingCount++
    },
    FINISH_LOADING_FEEDBACK_FORM_URL(state) {
      state.feedbackFormUrlLoadingCount--
    },
    START_SUBMITTING_QUICK_FEEDBACK(state) {
      state.quickFeedbackSubmittingCount++
    },
    FINISH_SUBMITTING_QUICK_FEEDBACK(state) {
      state.quickFeedbackSubmittingCount--
    },
    UPSERT_REPLACE_ME_STATUS_CHECK_FOR_CLIENT(state, { payload, clientId }) {
      let found = state.replaceMeStatusChecks.find(
        (x) => x.clientId === clientId
      )

      if (found) {
        found = payload
        found.timestamp = dayjs()
        return
      }

      const statusCheck = {
        ...payload,
        clientId,
        timestamp: dayjs(),
      }

      state.replaceMeStatusChecks.push(statusCheck)
    },
    REFRESH_HOME(state) {
      state.homeRefreshCount++
    },
    SET_ITEMS(state, payload) {
      if (payload.append && state.bookingList?.bookings?.length) {
        state.bookingList = {
          ...payload,
          bookings: [...state.bookingList.bookings, ...payload.bookings],
        }
      } else {
        state.bookingList = payload
      }
    },
    START_LOADING_EXPORT_FILE(state) {
      state.exportFileCount++
    },
    FINISH_LOADING_EXPORT_FILE(state) {
      state.exportFileCount--
    },
    START_LOADING_FETCH_BOOKING(state) {
      state.fetchBookingCount++
    },
    FINISH_LOADING_FETCH_BOOKING(state) {
      state.fetchBookingCount--
    },
    SET_EXPORT_DATA(state, payload) {
      state.exportData = payload
    },
  },
  actions: {
    setRecentLocation({ commit }, locationId) {
      commit('SET_RECENT_BOOKING_LOCATION', locationId)
    },
    async loadBookingsByClientAndDate({ rootGetters, commit }, payload) {
      return await new VuexResponse(commit)
        .request(() =>
          this.$api.bookings.getSummaryBookingsByDate(
            rootGetters['client/getSelectedClients'],
            payload
          )
        )
        .withLoading(
          'START_LOADING_SELECTED_DATE_BOOKINGS',
          'FINISH_LOADING_SELECTED_DATE_BOOKINGS'
        )
        .withFailureToast('Cannot load bookings')
        .go()
    },
    async loadYearOfBookingOverviewData({ commit }, payload) {
      return await new VuexResponse(commit)
        .request(() =>
          this.$api.bookings.getBookingOverview(payload.clientIds, payload.year)
        )
        .withLoading(
          'START_LOADING_BOOKINGS_OVERVIEW',
          'FINISH_LOADING_BOOKINGS_OVERVIEW'
        )
        .withFailureToast('Cannot load bookings')
        .withCommit('SET_BOOKING_OVERVIEW')
        .go()
    },
    async submitBooking({ commit }, payload) {
      commit('START_LOADING_CRUD')

      try {
        const response = await this.$api.bookings.post('', payload)

        if (isSuccess(response.status)) {
          return success(response.data)
        }
      } catch (ex) {
        return fail(ex.response.data)
      } finally {
        commit('FINISH_LOADING_CRUD')
      }
    },
    async editBooking({ commit }, payload) {
      return await new VuexResponse(commit)
        .request(() => this.$api.bookings.editBooking(payload))
        .withLoading(
          'START_LOADING_EDIT_BOOKING',
          'FINISH_LOADING_EDIT_BOOKING'
        )
        .withFailureToast(this.$i18n.t('error.genericApiError'))
        .go()
    },
    async requestFeedbackFormUrl({ commit }, payload) {
      commit('START_LOADING_FEEDBACK_FORM_URL')
      try {
        const response = await this.$api.bookings.getFeedbackFormUrl(
          payload.bookingId
        )

        if (isSuccess(response.status)) {
          return success(response.data)
        }
      } catch (ex) {
        toast.error('Cannot load feedback form url')
        return fail()
      } finally {
        commit('FINISH_LOADING_FEEDBACK_FORM_URL')
      }
    },
    // TODO: This function will likely be reworked to accept a passed in
    //       client ID
    async submitQuickFeedback({ commit, dispatch, rootGetters }, payload) {
      commit('START_SUBMITTING_QUICK_FEEDBACK')
      try {
        const response = await this.$api.bookings.submitQuickFeedback(
          payload.bookingId,
          payload.data
        )

        if (isSuccess(response.status)) {
          await dispatch(
            'pendingfeedbacks/setFeedbackAsComplete',
            {
              bookingId: payload.bookingId,
              clientId: rootGetters['client/getSelectedClients'][0], // TODO: Remove
            },
            { root: true }
          )
          return success()
        }
      } catch (ex) {
        return fail(ex.response.data)
      } finally {
        commit('FINISH_SUBMITTING_QUICK_FEEDBACK')
      }
    },
    // TODO: This function will likely be reworked to accept a passed in
    //       client ID
    async checkReplaceMeStatus(
      { commit, getters, rootGetters },
      forceRefresh = false
    ) {
      const cId = rootGetters['client/getSelectedClients'][0] // TODO: Remove

      // Check if user has access to replace me
      if (
        !rootGetters['auth/hasReplaceMePermissionForAtleastOneClientLocation'](
          cId
        )
      )
        return success()

      // Check cached replace me status
      const isCached = getters.replaceMeStatusChecks(cId)

      if (
        isCached &&
        isCacheFresh({
          cacheDuration: 5,
          durationUnits: 'minutes',
          lastUpdated: isCached?.timestamp,
          forceRefresh,
        })
      )
        return Promise.resolve(success(isCached))

      commit('START_LOADING_REPLACE_ME_STATUS_CHECK')

      try {
        const response = await this.$api.bookings.checkReplaceMeStatus(cId)

        if (isSuccess(response.status)) {
          commit('UPSERT_REPLACE_ME_STATUS_CHECK_FOR_CLIENT', {
            payload: response.data,
            clientId: cId,
          })
          return success(response.data)
        }
      } catch (ex) {
        // Ignore alert if 403 since the user doesn't have permission for replace me
        // and likely doesn't need to be notified they don't. Would just be alert spam
        if (!isHttpStatus(ex.response.status, 'Forbidden'))
          toast.error(
            ex.response?.data?.message
              ? ex.response.data?.message
              : this.$i18n('replaceMe.checkReplaceMeStatusErrorGenericText')
          )
        return fail()
      } finally {
        commit('FINISH_LOADING_REPLACE_ME_STATUS_CHECK')
      }
    },
    async submitReplaceMeBooking({ commit }, payload) {
      commit('START_LOADING_SUBMIT_REPLACE_ME')

      try {
        const response = await this.$api.bookings.submitReplaceMeBooking(
          payload
        )

        if (isSuccess(response.status)) {
          const selectedDay = getSelectedDayText(
            payload.selectedDay,
            payload.isNextMonday
          )
          toast.success(
            this.$i18n.t('replaceMe.successNotificationText', { selectedDay })
          )
          return success(response.data)
        }
      } catch (ex) {
        toast.error(
          ex.response?.data?.message
            ? ex.response.data?.message
            : this.$i18n.t('error.genericFailedRequestMessage')
        )
        return fail()
      } finally {
        commit('FINISH_LOADING_SUBMIT_REPLACE_ME')
      }
    },
    async cancelBooking({ commit }, payload) {
      commit('START_LOADING_CANCEL_BOOKING')

      try {
        const response = await this.$api.bookings.cancelBooking(
          payload.bookingId,
          {
            cancelReason: payload.cancelReason,
          }
        )

        if (isSuccess(response.status)) {
          toast.success(
            this.$i18n.t('booking.cancelBookingSuccessText', {
              date: payload.date,
            })
          )
          return success(response.data)
        }
      } catch (ex) {
        toast.error(
          ex.response?.data?.message
            ? ex.response.data?.message
            : this.$i18n.t('error.genericFailedRequestMessage')
        )
        return fail()
      } finally {
        commit('FINISH_LOADING_CANCEL_BOOKING')
      }
    },
    async getExtendBookingCandidatesAndSchedule(
      { commit },
      { bookingId, endDateLocal }
    ) {
      return await new VuexResponse(commit)
        .request(() =>
          this.$api.bookings.getExtendBookingCandidatesAndSchedule(
            bookingId,
            endDateLocal
          )
        )
        .withLoading(
          'START_LOADING_EXTEND_BOOKING',
          'FINISH_LOADING_EXTEND_BOOKING'
        )
        .withFailureToast(this.$i18n.t('error.genericApiError'))
        .go()
    },
    async extendBooking({ commit }, { bookingId, payload }) {
      return await new VuexResponse(commit)
        .request(() => this.$api.bookings.extendBooking(bookingId, payload))
        .withLoading(
          'START_LOADING_EXTEND_BOOKING',
          'FINISH_LOADING_EXTEND_BOOKING'
        )
        .withSuccessToast(this.$i18n.t('booking.extend.extendedToast'))
        .go()
    },
    async getBookingDetailById({ commit }, bookingId) {
      return await new VuexResponse(commit)
        .request(() => this.$api.bookings.getBookingDetailById(bookingId))
        .withLoading(
          'START_LOADING_FETCH_BOOKING',
          'FINISH_LOADING_FETCH_BOOKING'
        )
        .withFailureToast(this.$i18n.t('error.genericApiError'))
        .go()
    },
    async getBookings({ commit }, payload) {
      const result = await new VuexResponse(commit)
        .request(() => this.$api.searchBookings.search(payload))
        .withLoading('START_LOADING', 'FINISH_LOADING')
        .withFailureToast(this.$i18n.t('booking.failedToLoadBookingsErrorText'))
        .go()
        .catch(() => {
          return { isSuccess: false, data: null }
        })

      if (isSuccess) {
        if (result && result.isSuccess && result.data) {
          const append = payload.pageNumber && payload.pageNumber > 1
          commit('SET_ITEMS', { ...result.data, append })
        } else {
          toast.error(this.$i18n.t('booking.failedToLoadBookingsErrorText'))
        }
      }
      return result
    },
    async getExportData({ commit }, payload) {
      const result = await new VuexResponse(commit)
        .request(() => this.$api.searchBookings.getExportBookingData(payload))
        .withLoading('START_LOADING_EXPORT_FILE', 'FINISH_LOADING_EXPORT_FILE')
        .withFailureToast(this.$i18n.t('error.errorGenericApiErrorText'))
        .go()
        .catch(() => {
          return { isSuccess: false, data: null }
        })

      if (isSuccess) {
        if (result && result.isSuccess && result.data) {
          commit('SET_EXPORT_DATA', result.data)
          return result.data
        } else {
          toast.error(this.$i18n.t('error.errorGenericApiErrorText'))
        }
      }
      return result
    },
    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },
  },
}
